// ./src/components/features/EventVendors/VendorDocumentParts/VendorDocumentParts.tsx
//
// ROADMAP #170 — the pieces of a vendor document every view of one shows the same way: its number, when it is valid
// (issued · expires, in red when it expires before the event's last day, or has already expired), its files (each opens
// in a new tab — a PDF or a picture opens in the browser), and who put it on file. Used by the documents list (a table
// cell or a card row) and by `VendorDocumentCard` (the review drawer), so the three say the same words.
import React from "react";
import { Stack, Group, Text, Badge, Anchor } from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { VendorDocument, VENDOR_FILE_MIME } from "@/utils/api";
import { AppIcon } from "@/components/generic";

import { formatVendorDay } from "../vendorUi";

/** "86 KB", "1.2 MB". */
export const fileSizeText = (bytes?: number | null): string =>
  bytes ? (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`) : "";

/**
 * Its last valid day has passed (on this device's calendar — a document's dates are calendar dates, with no clock).
 * ⓘ On an event, `expiresBeforeEvent` (decided by the server on the EVENT's calendar) is the stronger statement and wins.
 */
export const documentExpired = (doc: VendorDocument): boolean => !!doc.expiresOn && doc.expiresOn < dayjs().format("YYYY-MM-DD");

/** "No. 12-345" — nothing when it has no number. */
export const DocumentNumber: React.FC<{ document: VendorDocument }> = ({ document: doc }) => {
  const { t } = useTranslation(["vendors"]);

  return doc.referenceNo ? <Text size="md">{t("vendors:document.number", { defaultValue: "No. {{n}}", n: doc.referenceNo })}</Text> : null;
};

/** Issued · expires, and why that matters here. */
export const DocumentValidity: React.FC<{ document: VendorDocument }> = ({ document: doc }) => {
  const { t } = useTranslation(["vendors"]);
  const warn = !!doc.expiresBeforeEvent || documentExpired(doc);

  return (
    <Stack gap={4}>
      <Group gap="md">
        {doc.issuedOn ? (
          <Text size="md" c="dimmed">
            {t("vendors:document.issued", { defaultValue: "Issued {{date}}", date: formatVendorDay(doc.issuedOn) })}
          </Text>
        ) : null}
        {doc.expiresOn ? (
          <Text size="md" c={warn ? "red" : "dimmed"} fw={warn ? 600 : undefined}>
            {t("vendors:document.expires", { defaultValue: "Expires {{date}}", date: formatVendorDay(doc.expiresOn) })}
          </Text>
        ) : (
          <Text size="md" c="dimmed">
            {t("vendors:document.noExpiry", { defaultValue: "No expiry date" })}
          </Text>
        )}
      </Group>
      {doc.expiresBeforeEvent ? (
        <Group>
          <Badge size="md" variant="light" color="red">
            {t("vendors:document.expiresBeforeEvent", { defaultValue: "Expires before the event" })}
          </Badge>
        </Group>
      ) : documentExpired(doc) ? (
        <Group>
          <Badge size="md" variant="light" color="red">
            {t("vendors:document.expired", { defaultValue: "Expired" })}
          </Badge>
        </Group>
      ) : null}
    </Stack>
  );
};

/** Each file as a link that opens it, with its size. */
export const DocumentFiles: React.FC<{ document: VendorDocument }> = ({ document: doc }) => {
  const { t } = useTranslation(["vendors"]);
  const files = doc.files ?? [];

  if (files.length === 0) {
    return null;
  }

  return (
    <Stack gap={4}>
      {files.map((f, i) => (
        <Group key={f.id ?? i} gap={6} wrap="nowrap">
          <AppIcon iconName={f.uploadedFile?.mimeType === VENDOR_FILE_MIME.PDF ? "FileTypePdf" : "Photo"} size={18} />
          {f.uploadedFile?.fileUrl ? (
            <Anchor
              href={f.uploadedFile.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              size="md"
              style={{ minWidth: 0 }}
              truncate
              // A tap on a link inside a swipeable card opens the file, never starts a swipe.
              onClick={(e) => e.stopPropagation()}
            >
              {f.uploadedFile.originalFilename || t("vendors:document.file", { defaultValue: "File {{n}}", n: i + 1 })}
            </Anchor>
          ) : (
            <Text size="md" c="dimmed">
              {t("vendors:document.fileGone", { defaultValue: "File not available" })}
            </Text>
          )}
          {f.uploadedFile?.sizeBytes ? (
            <Text size="md" c="dimmed" style={{ flexShrink: 0 }}>
              {fileSizeText(f.uploadedFile.sizeBytes)}
            </Text>
          ) : null}
        </Group>
      ))}
    </Stack>
  );
};

/** "Added by your team" / "Sent by the vendor". */
export const DocumentSource: React.FC<{ document: VendorDocument }> = ({ document: doc }) => {
  const { t } = useTranslation(["vendors"]);

  return (
    <Text size="md" c="dimmed">
      {doc.submittedVia === "crew"
        ? t("vendors:document.byCrew", { defaultValue: "Added by your team" })
        : t("vendors:document.byVendor", { defaultValue: "Sent by the vendor" })}
    </Text>
  );
};
